import {
  formatDateShort,
  formatDayLong,
  formatMonthDayCompact,
  formatTimeRangeCompact,
} from './dates.js';
import { effectiveRoster } from './roster.js';
import type { Availability, Fill, GroupConfig, Override, Session } from './types.js';

export interface Reminder {
  subject: string;
  body: string;
}

/**
 * What the group calls its night. Shared with the calendar renderer rather than spelled out
 * twice: the chat reads "Tuesday permanent court time" every week, and a calendar event that
 * named the same thing differently would read as a different thing.
 */
export function courtTimePhrase(dayLabel: string): string {
  return `${dayLabel} permanent court time`;
}

/**
 * The body is the message and nothing else (§4.1) — no greeting, no preamble — so select-all-copy
 * on a phone yields exactly what gets pasted into the group chat. The closing line is part of
 * that message rather than a footer: it is what lets the rest of it stay this short.
 *
 * Full names and the court time, and that is the whole of it. Subs, sitters and short-handed
 * weeks are all the same loose end — who is actually coming — and the closing line puts it
 * where it belongs: finding a sub is the player's own errand, off the app entirely, and the app
 * is only where the answer gets posted for everyone else. So a sub is simply named in the slot
 * they cover, a fill-in found for a short-handed week is simply named after them, and a week
 * still waiting on one is four bullets short one bullet.
 *
 * Returns `null` for a `no-session` date or one that isn't in the schedule, which is how the
 * Reminder Lambda stays quiet on holiday weeks without special-casing its cron.
 */
export function renderReminder(
  group: GroupConfig,
  session: Session | undefined,
  overrides: Override[],
  availabilities: Availability[] = [],
  fills: Fill[] = [],
): Reminder | null {
  if (!session || session.status !== 'scheduled') return null;

  const roster = effectiveRoster(group, session, overrides, availabilities, fills);
  // Full names, as the roster already carries them: this is a lineup being read down, not a
  // sentence being read out loud, and an outsider's name arrives with no first/last to split.
  const playing = roster.playing.map((slot) => slot.name);

  // `endTime` is when the court booking ends, so it holds even on a week whose start was moved.
  const when = `${formatDayLong(session.date)} (${formatMonthDayCompact(session.date)})`;
  const courtTime = formatTimeRangeCompact(roster.time, group.endTime);

  const body = [
    `Reminder for ${courtTimePhrase(when)}, ${courtTime}:`,
    ...playing.map((name) => `  • ${name}`),
    '',
    'Arrange your own sub as needed, then record it at topspinapp.com.',
  ].join('\n');

  return {
    subject: `[Topspin] ${formatDateShort(session.date)} — ${group.groupId} group`,
    body,
  };
}
